import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { FoundationalTask, GrowthTask } from "./useMyTasks/types";

interface TaskWithBrand {
  brand: {
    id: string;
    name: string;
    slug?: string;
  };
}

export type FoundationalTaskWithBrand = FoundationalTask & TaskWithBrand;
export type GrowthTaskWithBrand = GrowthTask & TaskWithBrand;

interface UseAllTasksAllBrandsOptions {
  enabled?: boolean;
}

export const useAllTasksAllBrands = (options: UseAllTasksAllBrandsOptions = {}) => {
  const { enabled = true } = options;
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const getUserAccessibleBrandIds = async (): Promise<string> => {
    if (!user) return '';

    try {
      const { data: ownedBrands } = await supabase
        .from('brands')
        .select('id')
        .eq('user_id', user.id);

      const { data: accessibleBrands } = await supabase
        .from('user_brand_access')
        .select('brand_id')
        .eq('user_id', user.id);

      const allBrandIds = [
        ...(ownedBrands || []).map(b => b.id),
        ...(accessibleBrands || []).map(b => b.brand_id)
      ];

      return allBrandIds.join(',');
    } catch (error) {
      return '';
    }
  };

  const fetchFoundationalTasksAllBrands = async (): Promise<FoundationalTaskWithBrand[]> => {
    if (!user) return [];

    const accessibleBrandIds = await getUserAccessibleBrandIds();
    
    if (!accessibleBrandIds) return [];

    const brandIdsArray = accessibleBrandIds.split(',');

    const { data: squadData } = await supabase
      .from('growth_squad')
      .select('brand_id, type')
      .eq('user_id', user.id)
      .in('brand_id', brandIdsArray);

    const growthPartnerBrands = squadData
      ?.filter(squad => squad.type === 'Growth Partner')
      .map(squad => squad.brand_id) || [];

    let query = supabase
      .from('foundational_tasks')
      .select(`
        *,
        brands (
          id,
          name,
          slug
        ),
        growth_sprints!foundational_tasks_sprint_id_fkey (
          id,
          name
        )
      `)
      .in('brand_id', brandIdsArray)
      .is('parent_id', null);

    if (growthPartnerBrands.length < brandIdsArray.length) {
      query = query.or(`internal.eq.false,and(internal.eq.true,brand_id.in.(${growthPartnerBrands.join(',')}))`);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    
    return (data || []).map((task: any) => ({
      ...task,
      brand: task.brands ? {
        id: task.brands.id,
        name: task.brands.name,
        slug: task.brands.slug
      } : { id: '', name: 'Unknown Brand' },
      sprint: task.growth_sprints ? {
        id: task.growth_sprints.id,
        name: task.growth_sprints.name
      } : undefined
    }));
  };

  const fetchGrowthTasksAllBrands = async (): Promise<GrowthTaskWithBrand[]> => {
    if (!user) return [];

    const accessibleBrandIds = await getUserAccessibleBrandIds();
    
    if (!accessibleBrandIds) return [];

    const brandIdsArray = accessibleBrandIds.split(',');

    const { data, error } = await supabase
      .from('growth_tasks')
      .select(`
        *,
        brands!fk_growth_tasks_brand (
          id,
          name,
          slug
        ),
        sales_channels!fk_growth_tasks_sales_channel (
          id,
          name
        ),
        growth_sprints!fk_growth_tasks_sprint_id (
          id,
          name
        )
      `)
      .in('brand_id', brandIdsArray)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // Batch fetch all unique submitted_by squad members in a single query (fixes N+1)
    const uniqueSubmittedByIds = [...new Set(
      (data || [])
        .map((task: any) => task.submitted_by)
        .filter(Boolean)
    )];

    let squadMap: Record<string, { name: string; alias: string; email: string }> = {};

    if (uniqueSubmittedByIds.length > 0) {
      const { data: squadMembers } = await supabase
        .from('growth_squad')
        .select('id, name, alias, email')
        .in('id', uniqueSubmittedByIds);
      
      if (squadMembers) {
        squadMap = squadMembers.reduce((acc, member) => {
          acc[member.id] = { name: member.name, alias: member.alias, email: member.email };
          return acc;
        }, {} as Record<string, { name: string; alias: string; email: string }>);
      }
    }

    const tasksWithBrandAndInfo = (data || []).map((task: any) => ({
      ...task,
      brand: task.brands ? {
        id: task.brands.id,
        name: task.brands.name,
        slug: task.brands.slug
      } : { id: '', name: 'Unknown Brand' },
      growth_squad: task.submitted_by ? squadMap[task.submitted_by] || null : null,
      sales_channel: task.sales_channels ? {
        id: task.sales_channels.id,
        name: task.sales_channels.name
      } : undefined,
      sprint: task.growth_sprints ? {
        id: task.growth_sprints.id,
        name: task.growth_sprints.name
      } : undefined,
      status: task.task_status || 'pending'
    }));
    
    return tasksWithBrandAndInfo;
  };

  // Use React Query for foundational tasks
  const {
    data: foundationalTasks = [],
    isLoading: isLoadingFoundational,
    error: foundationalError
  } = useQuery({
    queryKey: ['all-tasks', 'foundational', user?.id],
    queryFn: fetchFoundationalTasksAllBrands,
    enabled: !!user && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
    refetchOnWindowFocus: false,
  });

  // Use React Query for growth tasks
  const {
    data: growthTasks = [],
    isLoading: isLoadingGrowth,
    error: growthError
  } = useQuery({
    queryKey: ['all-tasks', 'growth', user?.id],
    queryFn: fetchGrowthTasksAllBrands,
    enabled: !!user && enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Show error toast if any query fails
  if (foundationalError) {
    toast({
      title: "Error",
      description: "Failed to fetch foundational tasks",
      variant: "destructive",
    });
  }

  if (growthError) {
    toast({
      title: "Error",
      description: "Failed to fetch growth tasks",
      variant: "destructive",
    });
  }

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
  };

  return {
    foundationalTasks,
    growthTasks,
    isLoading: enabled ? (isLoadingFoundational || isLoadingGrowth) : false,
    refetch,
  };
};
